/**
 * Los tiempos de un plan, desglosados: viajar, atender y la jornada que suman.
 *
 * Existe porque el motor devuelve `total_duration_min` = **tiempo de viaje**
 * (`round(vroom_route.duration / 60)`, y en VROOM `route.duration` excluye el
 * servicio, que va en un campo aparte). La pantalla lo mostraba como "duración
 * total": medido sobre una corrida real, 35 h para una jornada de 92 h, porque la
 * atención al cliente es el 61% del día.
 *
 * La atención **no** hace falta pedirla a la API: el back escribe
 * `departure_time = arrival + service_s`, así que `departure − arrival` es
 * exactamente el `service_min` que el optimizador asignó a esa parada.
 *
 * Límite conocido: con ventanas horarias VROOM puede hacer **esperar** al camión,
 * y esa espera no es derivable del contrato actual (el tramo almacén→parada‑1
 * llega como `0`, así que no se puede aislar por diferencia). En ese caso la
 * jornada queda corta por el tiempo de espera. El arreglo —exponer `service` y
 * `waiting_time`, que VROOM ya manda— está documentado en el `design.md`.
 *
 * Módulo puro: entran horas y minutos, salen números. Sin HTTP, sin Angular.
 */

/** Lo mínimo que hace falta de una parada para derivar su atención. */
export interface StopTimes {
  arrivalTime?: string;
  departureTime?: string;
}

/** Tiempos de una ruta. `workMin` es lo que la operación realmente ocupa. */
export interface RouteTimes {
  travelMin: number;
  serviceMin: number;
  workMin: number;
}

/** Una ruta resumida, para promediar la corrida. */
export interface RouteTimeSummary {
  distanceKm: number;
  travelMin: number;
  serviceMin: number;
  stops: number;
}

/** Promedios de una corrida. La velocidad es `null` si no se puede calcular. */
export interface RunAverages {
  routes: number;
  stops: number;
  /** km ÷ horas de viaje de toda la flota. */
  fleetKmh: number | null;
  slowestKmh: number | null;
  fastestKmh: number | null;
  travelPerStopMin: number;
  servicePerStopMin: number;
  workPerRouteMin: number;
}

/**
 * `HH:MM` → minutos desde medianoche, o `null` si no es una hora válida.
 *
 * Exportada porque el recálculo de una ruta editada mide contra el fin del turno y
 * necesita la MISMA lectura del reloj: dos parsers de `HH:MM` divergen en los bordes
 * (`24:00`, `8:5`, espacios) sin dar error, y acá divergir significa declarar un
 * exceso de jornada que no existe o callar uno que sí.
 */
export function clockToMinutes(time?: string): number | null {
  return toMinutes(time);
}

/** `HH:MM` → minutos desde medianoche, o `null` si no es una hora válida. */
function toMinutes(time?: string): number | null {
  if (!time) return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/**
 * Minutos de atención de una parada: su salida menos su llegada.
 *
 * Devuelve `0` ante cualquier dato que no permita afirmarlo —hora ausente,
 * formato inesperado, salida anterior a la llegada— en vez de propagar un `NaN`
 * que después aparecería sumado en el tablero.
 *
 * Los segundos se pierden al venir en `HH:MM`, y no importa: `service_min` es un
 * entero de minutos en el contrato.
 */
export function serviceMinBetween(arrivalTime?: string, departureTime?: string): number {
  const from = toMinutes(arrivalTime);
  const to = toMinutes(departureTime);
  if (from === null || to === null) return 0;
  return Math.max(0, to - from);
}

/** Tiempos de una ruta a partir de sus paradas y su tiempo de viaje. */
export function routeTimes(
  stops: readonly StopTimes[] | undefined,
  travelMin: number,
): RouteTimes {
  const serviceMin = (stops ?? []).reduce(
    (total, s) => total + serviceMinBetween(s?.arrivalTime, s?.departureTime),
    0,
  );
  return { travelMin, serviceMin, workMin: travelMin + serviceMin };
}

/** Lo mínimo de una parada para aislar el tramo de vuelta. */
export interface StopLegs {
  /** Minutos de viaje desde la parada anterior (`0` en la primera, por contrato). */
  legDurationMin?: number;
  /** Minutos desde el inicio del turno. En la PRIMERA parada es el viaje de ida. */
  etaMin?: number;
}

/**
 * Minutos del viaje del almacén a la primera parada.
 *
 * Viene en el `legDurationMin` de la primera parada, **no en `0`** como decía el
 * contrato: el backend lo calcula como `step.duration - prev_duration` con
 * `prev_duration` arrancando en cero y salteando el paso `start` de VROOM, así que la
 * primera parada resta contra el origen y su tramo ES la ida. Medido: 22 min en una
 * ruta local, 153 en un territorio lejano.
 *
 * El `etaMin` queda como respaldo para el día en que el contrato cumpla lo que su
 * comentario prometía, y para no depender de un solo campo.
 */
export function outboundLegMin(stops: readonly StopLegs[] | undefined): number {
  const first = (stops ?? [])[0];
  if (!first) return 0;
  const leg = first.legDurationMin ?? 0;
  return leg > 0 ? leg : (first.etaMin ?? 0);
}

/**
 * Minutos del regreso al almacén.
 *
 * El tramo de vuelta **ya está contado** en la jornada: el vehículo que se le manda a
 * VROOM lleva `start` y `end` en el almacén, así que `total_duration_min` incluye ida,
 * tramos entre paradas y vuelta. Lo que no existía era **verlo**: se leen paradas hasta
 * media tarde y una jornada de casi nueve horas, sin ninguna fila donde se fueron las
 * que faltan.
 *
 * Se deriva por diferencia porque el contrato no lo trae aparte: el viaje total menos
 * todos los tramos informados. Como la ida ya está en el tramo de la primera parada
 * (ver `outboundLegMin`), sumarla otra vez por `etaMin` la contaba **dos veces** — daba
 * 2 minutos en una ruta cuyo regreso son 24, y en la ruta lejana daba negativo, así que
 * la fila no se mostraba justo donde más importaba.
 *
 * Nunca negativo: si los tramos informados ya cubren el viaje total, no hay vuelta que
 * mostrar, y un número negativo sería peor que ninguno.
 */
export function returnLegMin(stops: readonly StopLegs[] | undefined, travelMin: number): number {
  const list = stops ?? [];
  if (!list.length) return 0;
  const legsMin = list.reduce((total, s) => total + (s?.legDurationMin ?? 0), 0);
  // Si la primera parada no informa su tramo, la ida no está en la suma y hay que
  // agregarla desde el `etaMin`; si lo informa, ya está contada.
  const accountedMin = (list[0]?.legDurationMin ?? 0) > 0 ? legsMin : legsMin + outboundLegMin(list);
  return Math.max(0, travelMin - accountedMin);
}

/**
 * Hora de reloj resultante de sumar minutos a un `HH:MM`.
 *
 * Se usa para la llegada de vuelta al almacen, que el contrato no trae: la ultima
 * parada informa su salida y el regreso se suma encima. Da la vuelta en la medianoche
 * en vez de devolver `24:xx`, y devuelve `''` cuando la hora de partida no se puede
 * leer — un regreso a "NaN:NaN" es peor que no mostrarlo.
 */
export function addMinutesToClock(hhmm: string | undefined, minutes: number): string {
  const match = typeof hhmm === 'string' ? /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim()) : null;
  if (!match) return '';
  const total = Number(match[1]) * 60 + Number(match[2]) + Math.round(minutes);
  const wrapped = ((total % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Velocidad en km/h, o `null` si no hay tiempo de viaje contra el que medir. */
function kmh(distanceKm: number, travelMin: number): number | null {
  if (travelMin <= 0) return null;
  return distanceKm / (travelMin / 60);
}

/**
 * Promedios de la corrida.
 *
 * La velocidad es un **resultado observado**, no un parámetro: el servicio de
 * ruteo no recibe una velocidad, la deriva del mapa. Se calcula sobre el total de
 * la flota (no promediando las velocidades por ruta) para que una ruta corta no
 * pese lo mismo que una de 167 km.
 */
export function runAverages(routes: readonly RouteTimeSummary[]): RunAverages {
  const list = (routes ?? []).filter((r) => !!r);
  const stops = list.reduce((n, r) => n + (r.stops || 0), 0);
  const travelMin = list.reduce((n, r) => n + (r.travelMin || 0), 0);
  const serviceMin = list.reduce((n, r) => n + (r.serviceMin || 0), 0);
  const distanceKm = list.reduce((n, r) => n + (r.distanceKm || 0), 0);

  // Solo las rutas que rodaron entran al rango: una de 0 minutos no tiene velocidad.
  const speeds = list
    .map((r) => kmh(r.distanceKm || 0, r.travelMin || 0))
    .filter((v): v is number => v !== null);

  return {
    routes: list.length,
    stops,
    fleetKmh: kmh(distanceKm, travelMin),
    slowestKmh: speeds.length ? Math.min(...speeds) : null,
    fastestKmh: speeds.length ? Math.max(...speeds) : null,
    travelPerStopMin: stops > 0 ? travelMin / stops : 0,
    servicePerStopMin: stops > 0 ? serviceMin / stops : 0,
    workPerRouteMin: list.length > 0 ? (travelMin + serviceMin) / list.length : 0,
  };
}

/** Lo que se necesita de una parada para saber cuánto tiempo del día consume. */
export interface StopCostInput {
  /** Viaje desde la parada anterior. Llega en `0` en la primera, por contrato. */
  legDurationMin?: number;
  serviceMin?: number;
  /** Minutos desde el inicio del turno hasta llegar. */
  etaMin?: number;
}

/** Viaje y atención de una parada, con su total. */
export interface StopTimeCost {
  travelMin: number;
  serviceMin: number;
  totalMin: number;
}

/**
 * Cuánto tiempo del día consume una parada: llegar hasta ella más atenderla.
 *
 * **Dato informativo**: no altera lo que se envía al motor ni ningún cálculo del
 * plan; se deriva de valores que la respuesta ya trae.
 *
 * La primera parada es la excepción y por eso esto no es aritmética de template:
 * su `legDurationMin` llega en `0` porque el motor no reporta el tramo
 * almacén→parada 1 como leg. Ese trayecto sí está en `etaMin`, que mide desde el
 * inicio del turno hasta la llegada. Sin la excepción, la primera parada
 * mostraría solo su atención y escondería el tramo de salida, que suele ser el
 * más largo del día.
 *
 * Con ventanas horarias, `etaMin` de la primera parada incluye la espera además
 * del viaje: el contrato no permite separarlas desde el front.
 */
export function stopTimeCost(stop: StopCostInput | undefined, isFirst: boolean): StopTimeCost {
  const serviceMin = stop?.serviceMin ?? 0;
  const travelMin = isFirst ? (stop?.etaMin ?? 0) : (stop?.legDurationMin ?? 0);
  return { travelMin, serviceMin, totalMin: travelMin + serviceMin };
}
